import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  CreateBranchRegionRequest,
  branchRegionsApi,
} from '@texas/api/endpoints/branchRegionsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { ReactSelectOption } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect } from 'react';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';

export interface CreateBranchRegionProps extends SharedDisclosureProps {
  onCreated: () => void;
}

const defaultValues: CreateBranchRegionRequest = {
  name: '',
  branchIds: [],
};

export function CreateBranchRegion({
  onCreated,
  onClose,
  isOpen,
}: CreateBranchRegionProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<CreateBranchRegionRequest>({
    defaultValues,
  });

  const { t } = useTranslation();
  const toast = useToast();

  const {
    request: createBranchRegionRequest,
    loading,
    error,
  } = useApiRequest(branchRegionsApi.create);

  const {
    branchesData: [branches, getBranches, loadingBranches],
  } = useMetadata();

  useEffect(() => {
    if (isOpen) {
      getBranches();
      reset(defaultValues);
    }
  }, [isOpen, getBranches, reset]);

  const onSubmit = async (data: CreateBranchRegionRequest) =>
    await request(createBranchRegionRequest, [data], () => {
      toast({
        title: t('general.created'),
        status: 'success',
        isClosable: true,
      });
      onCreated();
      onClose();
    });

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('branchRegion.create')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired={true} isInvalid={!!errors.name}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('name', {
                    required: t('errors.nameRequired', {
                      name: t('general.name'),
                    }),
                    maxLength: {
                      value: 100,
                      message: t('errors.nameMaxLength', {
                        name: t('general.name'),
                        count: 100,
                      }),
                    },
                  })}
                />
                <HookFormCounter
                  control={control}
                  name="name"
                  maxLength={100}
                />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>

              <FormControl>
                <TexasFormLabel>{t('general.branches')}</TexasFormLabel>
                <Controller
                  name="branchIds"
                  control={control}
                  render={({ field }) => (
                    <TexasSelect
                      {...field}
                      isLoading={loadingBranches}
                      isClearable={true}
                      value={field.value.map<ReactSelectOption>((x) => ({
                        label: branches.find((b) => b.id === x)?.name ?? '',
                        value: x,
                      }))}
                      isMulti={true}
                      onChange={(e) => field.onChange(e.map((x) => x.value))}
                      options={branches.map<ReactSelectOption>((x) => ({
                        value: x.id,
                        label: `${x.name} (${x.identifier})`,
                      }))}
                      closeMenuOnSelect={false}
                    />
                  )}
                />
              </FormControl>

              <ErrorDetails error={error} />

              <Flex gap={2} justifyContent="end" pt={4}>
                <SubmitButton loading={loading}>
                  {t('general.create')}
                </SubmitButton>
              </Flex>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
